/**
 * Training SOP — content model.
 *
 * This is the SINGLE SOURCE for the training material. It renders two ways:
 *   1. The in-app Training page (access-aware, deep-linkable, searchable).
 *   2. The Word (.docx) export for document control.
 *
 * Both outputs read these same structures, so the on-screen guide and the
 * controlled document can never drift apart. When a feature ships, update the
 * content here and both regenerate — never hand-edit the .docx.
 *
 * House style (agreed with the product owner, 2026-07-25) — the "reasonable medium" between
 * click-by-click and pure prose:
 *   summary  — 1–2 plain-English sentences: what this is and why it matters.
 *   steps    — 3–6 outcome-level steps that NAME real UI landmarks (tab names,
 *              button labels, the one or two fields that matter) but do not
 *              narrate every keystroke. Click-by-click ages badly; naming the
 *              landmark survives a layout tweak.
 *   watchOut — 0–2 bullets, ONLY where something genuinely trips people up.
 *              Resist padding this; a gotcha on every task trains people to
 *              skip them.
 *   deepLink — replaces the screenshot. Because this lives inside the app we
 *              can send the reader to the real screen, which can't go stale.
 *
 * NAMING (product owner, 2026-07-25): the product is always "the Project Tracker" in
 * user-facing copy. Never "PMT" — that's our internal shorthand and means
 * nothing to a PM or a field leader.
 *
 * OWNERSHIP: say who does a thing when it isn't the reader. Several tasks look
 * like anyone's job but aren't. Naming the owner turns "why can't I do this"
 * into "who do I ask" — see OWNERSHIP below for the authoritative map.
 */

/**
 * Who OWNS each area of the work at E&W (product owner, 2026-07-25).
 *
 * This is org practice, not a permission model — the code can only tell us who
 * *can* do a thing, never who *should*. Both errors caught in the first review
 * came from that gap, so it's recorded here once and applied via `Topic.owner`
 * rather than re-derived per track.
 *
 * Where the reader isn't the owner, write the topic as "here's what this means
 * for you and who to chase", not as a procedure they can't carry out.
 */
export const OWNERSHIP = {
  /** Estimator sets the job up in Intacct, so they hold the real job number. */
  jobNumber: 'Estimator',
  /**
   * Estimated labor hours. TYPED by the estimator at job set-up since 2026-08-04
   * — the Intacct figure that used to fill it was autocalculated and ran low
   * (product owner), so the app was showing a number nobody stood behind.
   */
  laborHours: 'Estimator',
  /** PMs own it; the Director of Preconstruction monitors progress. */
  mobilizationAndChecklist: 'PM (Director of Preconstruction monitors)',
  /** Written by the crew on site. */
  dailyLogs: 'Field Leader',
  /** PM-owned with PE support — same for submittals and the schedule. */
  rfis: 'PM (PE assists)',
  submittals: 'PM (PE assists)',
  schedule: 'PM (PE assists)',
  /**
   * Approved concrete mix IDs. Director of Preconstruction, 2026-07-29:
   * "Typically the PM is responsible for sending the concrete submittals and
   * confirming approved Mix IDs." PEs hold the permission and can keep the list
   * tidy, but the approval decision is the PM's.
   */
  concreteMixDesigns: 'PM',
  /**
   * The job's concrete supplier and blanket POs. Two different answers behind
   * one card, so the label has to carry both:
   *
   *  - **Blanket PO — the estimator.** An estimator, 2026-08-21 (issue #368):
   *    "Typically, BPOs are created by the estimator. I would suggest changing
   *    the primary responsibility to estimator, secondary responsibility to PM
   *    and PE."
   *  - **Supplier — varies by job** (product owner, 2026-08-21). Sometimes the estimator
   *    has one lined up at bid; sometimes the PM chooses at buyout. ⚠️ Do NOT
   *    collapse this to a single owner. Issue #368 is about the PO only, and an
   *    earlier version of this constant over-applied it to the supplier as well
   *    — which told PMs to chase the estimator for a choice that was theirs.
   *
   * ⚠️ Also deliberately SEPARATE from concreteMixDesigns, even though both
   * live on the QA/QC tab and this topic used to borrow that constant.
   * Approving a Mix ID is the PM's call (Director of Preconstruction,
   * 2026-07-29); none of that reasoning transfers here.
   */
  concreteSupplierAndBpo: 'Estimator for the PO; supplier varies by job',
  /** Only an admin can trigger a Sage sync; otherwise nightly at 3 AM. */
  sageSync: 'Admin',
  /**
   * The PPR Financial Data tab. Accounting enters the figures while a review
   * sits in Awaiting Financials — `ppr.financials.edit` was created for
   * exactly that (issue #312) — and the coordinator can enter or correct
   * them too.
   */
  pprFinancials: 'Accounting (PPR coordinator can assist)',
  /**
   * Crew requests (#303, September 2026). The primary PM raises them on a won
   * or real job; on a SYSTAC bid it is the ESTIMATOR's forecast update
   * (decision 1, product owner with operations, 2026-08-31). Schedulers decide them
   * and book crew BY HAND — an approved request never writes the schedule
   * (operations: "to ensure all context is captured that was discussed during the
   * approval process").
   */
  manpowerRequests: 'PM raises; Estimator on a SYSTAC bid; Scheduler books by hand',
  /**
   * The Start ≠ Crew flag (#385). Split ownership is the whole point of it: the
   * planned start and the crew days answer the same question and belong to
   * different people, and the flag exists because neither could see the
   * other's answer. So the chip names both, and neither topic that uses it is
   * written as if the reader could fix it alone.
   */
  startAlignment: 'PM owns the planned start; Scheduler owns the crew days',
} as const;

// ─── Access ───────────────────────────────────────────────────────────────────

/**
 * Who should see a piece of content. Evaluated against the signed-in user so a
 * reader is never trained on buttons they can't click.
 *
 * Mirrors the app's real gating rules rather than inventing a parallel model:
 * Admin bypasses permission checks everywhere (see PermissionsGuard), and
 * "field-leader-only" is the app's own concept — a FieldLeader who is NOT an
 * office user (no Admin, no projects.edit, no canViewAll) collapses to a single
 * Projects tab. See App.tsx and ProjectWorkspace.tsx.
 *
 * An omitted rule means "everyone signed in". All present rules must pass (AND);
 * within a single list, any one entry is enough (OR).
 */
export interface AccessRule {
  /** Passes if the user holds ANY of these permission keys. Admin always passes. */
  anyPermission?: string[];
  /**
   * Passes only if the user holds EVERY one of these permission keys. Admin
   * always passes.
   *
   * `anyPermission` can only OR, so "needs A and B" had no way to be said. It
   * matters where one control is reachable only through another: Transfer Budget
   * renders only while Update Forecast is open, so a reader holding the transfer
   * permission alone would be taught a button they can never reach.
   */
  allPermissions?: string[];
  /** Passes if the user holds ANY of these role labels (exact label, e.g. 'FieldLeader'). */
  anyRole?: string[];
  /** Admin role only — used for genuinely admin-only surfaces. */
  adminOnly?: boolean;
  /**
   * Hide from field-leader-only users. Use for company-wide roll-ups they
   * cannot reach, so their guide doesn't describe navigation that isn't there.
   */
  hideFromFieldLeaderOnly?: boolean;
  /**
   * Additionally requires the admin "show project costs" toggle to be on.
   * Financials and Commitments are gated on BOTH a permission and this toggle.
   */
  requiresCostsEnabled?: boolean;
  /**
   * Requires at least one live project assignment — the Today tab's gate.
   *
   * ⚠️ The only rule here that is DATA rather than a permission or a role, and
   * Admin does NOT bypass it. There is no `PM` role in this system: being a PM
   * is an assignment, so Today asks the database who is on a live job rather
   * than reading a badge. Mirrors `hasActiveAssignments` on `/users/me`.
   */
  requiresActiveAssignment?: boolean;
}

// ─── Content pieces ───────────────────────────────────────────────────────────

/** A simple reference table (e.g. job-number prefixes → what's locked). */
export interface ContentTable {
  headers: string[];
  rows: string[][];
  /** Optional caption rendered above the table. */
  caption?: string;
}

/**
 * One trainable thing: a task someone performs, or a concept they must hold.
 * `id` is a stable slug — it becomes the in-app anchor (`?topic=<id>`) and the
 * Word bookmark, so treat it as a permanent identifier. Renaming an id breaks
 * saved links; change `title` instead.
 */
export interface Topic {
  id: string;
  title: string;
  /** 1–2 sentences. Plain English — assume no Project Tracker vocabulary yet. */
  summary: string;
  /** 3–6 outcome-level steps. Omit for pure-concept topics. */
  steps?: string[];
  /** 0–2 genuine gotchas. Omit rather than pad. */
  watchOut?: string[];
  /**
   * Who owns this work at E&W — a value from OWNERSHIP. Rendered as a small
   * "Owned by" chip. Set it whenever ownership isn't obvious from the track
   * you're in, and ALWAYS when the reader is not the owner.
   */
  owner?: string;
  /** Optional reference table. */
  table?: ContentTable;
  /**
   * In-app path to the live screen, e.g. '/projects' or '/reports/wip'.
   * Rendered as "Take me there →", which opens a SECOND tab so the guide stays
   * where the reader is (see TakeMeThere). Project-scoped screens can't be
   * linked without a job, so those use `deepLinkNote` instead.
   */
  deepLink?: string;
  /** Where to find it, when a deep link isn't possible (project-scoped tabs). */
  deepLinkNote?: string;
  /** Who this applies to. Omitted = everyone. */
  access?: AccessRule;
}

/** A group of topics within a track or module entry. */
export interface Section {
  id: string;
  title: string;
  /** Optional one-line framing for the section. */
  intro?: string;
  topics: Topic[];
  access?: AccessRule;
}

// ─── Role tracks ──────────────────────────────────────────────────────────────

export type TrackId =
  | 'pm'
  | 'pe'
  | 'estimator'
  | 'field-leader'
  | 'ops'
  | 'manpower'
  | 'ppr'
  | 'admin';

/**
 * A per-role learning path: what this person does day-to-day, in the order they
 * meet it. Tracks intentionally overlap — a PE and a PM both log daily reports —
 * because a track should stand alone rather than send readers hunting.
 */
export interface Track {
  id: TrackId;
  /** Display label, e.g. 'Project Manager'. */
  label: string;
  /** One line: who this track is for. Shown under the label. */
  audience: string;
  /**
   * Roles/permissions that suggest this track for the signed-in user, so the
   * page can preselect it. Suggestion only — it does not restrict anything.
   */
  suggestedFor?: AccessRule;
  /**
   * Who may OPEN this track at all. Omitted means everyone — reading another
   * role's guide is usually a good thing, and the topics inside carry their own
   * rules anyway.
   *
   * Set it only where the whole track should be invisible. The Administrator
   * track is the case (product owner, 2026-08-02: "there will only ever be one Admin, so
   * make sure ONLY the admin can view the Admin track"). Every topic in it was
   * already `adminOnly`, so a non-admin who selected it got an empty page —
   * they still saw the track exists, its audience line, and a blank body, which
   * reads like something failed to load rather than like a closed door.
   */
  access?: AccessRule;
  sections: Section[];
}

// ─── Module reference ─────────────────────────────────────────────────────────

/**
 * The lookup half of the SOP: one entry per feature area, for people who know
 * what they want and need the specifics. Role tracks teach the job; this
 * answers "what does this tab do".
 */
export interface ModuleEntry {
  id: string;
  /** The name as it appears in the UI, e.g. 'QA / QC'. */
  name: string;
  /** Where it lives, e.g. 'Project workspace ▸ Field' or 'Top nav'. */
  location: string;
  /** 1–2 sentences on purpose. */
  whatItsFor: string;
  topics: Topic[];
  access?: AccessRule;
}

// ─── Document root ────────────────────────────────────────────────────────────

export interface TrainingDoc {
  title: string;
  /** Bumped when content changes materially; stamped into the Word export. */
  revision: string;
  /** ISO date of the last content review. */
  revised: string;
  /**
   * Concepts everyone needs regardless of role (job-number locks, health,
   * checklist dots, scope toggle). Rendered before the tracks.
   */
  foundations: Section[];
  tracks: Track[];
  modules: ModuleEntry[];
  /**
   * Heading for the module appendix, in the document rather than in either
   * renderer — the page and the Word export both read it, and it is
   * user-facing content, so it belongs under the same revision guard as the
   * rest. Previously hard-coded identically in two files.
   */
  modulesTitle: string;
}
